import { PageHeader } from '@app/components/layout/page/components/page-header';
import Page from '@app/components/layout/page/page';
import { useAccount } from '@shared/api';
import {
  ExcludedCategorySummary,
  useGetExcludedItemsSummary,
} from '@shared/api/hooks/purchased-goods/excluded-items';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { getCurrencySymbol } from '@shared/lib/format';
import { formatNumber } from '@shared/lib/utils';
import { generatePath } from 'react-router-dom';
import { purchasedGoodsRoutes } from '../../purchased-goods-routes';
import ExcludedCategoryCard from '@app/components/excluded-items/excluded-category-card';
import { excludedItemsConfig } from '../tabs';

const INTRO_PARAGRAPHS = [
  'Your purchase reports will only include items that we can confidently model as either food or packaging. Non food or packaging items are intentionally excluded, along with items that could not be identified or that were missing appropriate weight information.',
  'To ensure as much of your purchase data as possible is assessed, we recommend reviewing these items and including additional information where possible.',
];

const formatSpend = (value: number, currency: string | null | undefined) =>
  `${getCurrencySymbol(currency)}${formatNumber(value)}`;

/** A right-aligned label + big value, with optional sub-line. */
function PageStat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="flex flex-col items-end gap-1">
      <Text variant="unit">{label}</Text>
      <Text className="text-4xl font-medium text-primary-800">{value}</Text>
      {sub && <Text variant="subtle">{sub}</Text>}
    </div>
  );
}

/**
 * Landing page for the Excluded items hub. Summarises how much purchase spend is
 * excluded and splits it into the two categories — "Items for review" (uncertain)
 * and "Other goods" — each linking to its own list.
 */
export default function ExcludedItemsLandingPage() {
  const { data: account } = useAccount(true);
  const currency = account?.defaultCurrency;
  const { data: summary } = useGetExcludedItemsSummary();

  const cards: {
    key: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    buttonLabel: string;
    to: string;
    stats: ExcludedCategorySummary;
    primary?: boolean;
  }[] = summary
    ? [
        {
          key: 'review',
          icon: <Icons.help className="size-5" />,
          title: excludedItemsConfig.review.title,
          description: excludedItemsConfig.review.description,
          buttonLabel: 'Review items',
          to: generatePath(
            purchasedGoodsRoutes.PURCHASED_EXCLUDED_ITEMS_REVIEW
          ),
          stats: summary.review,
          primary: true,
        },
        {
          key: 'other',
          icon: <Icons.cookingPot className="size-5" />,
          title: excludedItemsConfig.other.title,
          description: excludedItemsConfig.other.description,
          buttonLabel: 'View items',
          to: generatePath(purchasedGoodsRoutes.PURCHASED_EXCLUDED_ITEMS_OTHER),
          stats: summary.other,
        },
      ]
    : [];

  return (
    <Page name="Excluded items" title="Excluded items">
      <PageHeader
        name="Excluded items"
        breadcrumbs={[
          {
            label: 'Purchased goods',
            url: generatePath(purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK),
          },
          {
            label: 'Excluded items',
            url: generatePath(purchasedGoodsRoutes.PURCHASED_EXCLUDED_ITEMS),
          },
        ]}
      />

      {summary && (
        <div className="max-w-5xl">
          <div className="mb-8 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="flex max-w-xl flex-col gap-4">
              {INTRO_PARAGRAPHS.map((paragraph) => (
                <Text key={paragraph} variant="subtle">
                  {paragraph}
                </Text>
              ))}
            </div>
            {/* pr-6 matches the cards' p-6 so the header total aligns with the
                per-card counts below. */}
            <div className="flex shrink-0 gap-12 pr-6">
              <PageStat
                label="Excluded items"
                value={summary.total.count.toLocaleString('en-GB')}
              />
              {summary.total.spend != null && (
                <PageStat
                  label="Excluded spend"
                  value={formatSpend(summary.total.spend, currency)}
                  sub={
                    summary.excludedSpendProportion != null
                      ? `~${Math.round(summary.excludedSpendProportion * 100)}% of total spend`
                      : undefined
                  }
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {cards.map((card) => (
              <ExcludedCategoryCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                description={card.description}
                count={card.stats.count}
                spend={card.stats.spend}
                currency={currency}
                buttonLabel={card.buttonLabel}
                to={card.to}
                primary={card.primary}
              />
            ))}
          </div>
        </div>
      )}
    </Page>
  );
}
